import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import { BackIcon } from '../../components/AppHeader';
import ChevronRight from '@mui/icons-material/ChevronRight';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import { grey } from '@mui/material/colors';

const LS_KEY = '__app_quickscore';

export default function QuickScoreMatch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { matchIndex: matchIndexStr } = useParams();
  const idx = parseInt(matchIndexStr, 10);

  const [data, setData] = useState(() => {
    const saved = localStorage.getItem(LS_KEY);
    return saved ? JSON.parse(saved) : null;
  });

  const [showDelete, setShowDelete] = useState(false);

  useEffect(() => {
    if (data !== null) {
      localStorage.setItem(LS_KEY, JSON.stringify(data));
    }
  }, [data]);

  const matchData = data && data[idx];

  if (!matchData) {
    navigate('/app/quickscore', { replace: true });
    return null;
  }

  function onDelete() {
    const newData = [...data];
    newData.splice(idx, 1);
    localStorage.setItem(LS_KEY, JSON.stringify(newData));
    setData(newData);
    navigate('/app/quickscore');
  }

  function onNewStage() {
    const index = (matchData.stages || []).length;
    navigate('/app/quickscore/' + idx + '/' + index);
  }

  const stages = matchData.stages || [];

  return (
    <Box sx={{ pb: '80px', bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Hero header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
        color: 'white',
        px: 2, pt: 1.5, pb: 3,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <IconButton onClick={() => navigate('/app/quickscore')} sx={{ color: 'white', mr: 1 }}>
            <BackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {t('app.qsTitle')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {matchData.title}
            </Typography>
          </Box>
          <IconButton onClick={() => setShowDelete(true)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <Delete />
          </IconButton>
        </Box>

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 1.5, ml: 6 }}>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, px: 1.5, py: 0.5,
            backdropFilter: 'blur(4px)',
          }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', lineHeight: 1 }}>
              {t('app.qsStages')}
            </Typography>
            <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700 }}>
              {stages.length}
            </Typography>
          </Box>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, px: 1.5, py: 0.5,
            backdropFilter: 'blur(4px)',
          }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', lineHeight: 1 }}>
              {t('app.qsScores')}
            </Typography>
            <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700 }}>
              {stages.reduce((sum, s) => sum + (s.scores || []).length, 0)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Delete confirmation */}
      {showDelete && (
        <Paper sx={{
          textAlign: 'center', mx: 1.5, mt: 1, p: 2, borderRadius: 2.5,
          bgcolor: '#d32f2f', color: 'white',
        }}>
          <Typography variant="subtitle2" sx={{ color: 'inherit' }}>
            {t('app.qsDeleteMatch')}
          </Typography>
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={onDelete}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
            >
              {t('common.yes')}
            </Button>
            <Button
              variant="contained"
              onClick={() => setShowDelete(false)}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
            >
              {t('common.no')}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Stage cards */}
      <Box sx={{ px: 1.5, mt: -1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {stages.length === 0 ? (
          <Paper sx={{
            textAlign: 'center', py: 5, borderRadius: 2.5,
          }}>
            <Box sx={{
              width: 64, height: 64, borderRadius: '50%',
              bgcolor: grey[100], display: 'flex', alignItems: 'center', justifyContent: 'center',
              mx: 'auto', mb: 2,
            }}>
              <GpsFixedIcon sx={{ fontSize: 32, color: grey[400] }} />
            </Box>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
              {t('app.qsNewStage')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={onNewStage}
              sx={{ borderRadius: 2.5, py: 1, px: 3, textTransform: 'none', fontWeight: 600 }}
            >
              {t('app.qsNewStage')}
            </Button>
          </Paper>
        ) : (
          <>
            {stages.map((stage, stageIndex) => {
              const scoreCount = (stage.scores || []).length;
              return (
                <Paper key={stage.id || stageIndex} sx={{
                  borderRadius: 2.5, overflow: 'hidden',
                  opacity: stage.inactive ? 0.5 : 1,
                }}>
                  <ButtonBase
                    disabled={stage.inactive}
                    onClick={() => navigate('/app/quickscore/' + idx + '/' + stageIndex)}
                    sx={{ p: 0, textAlign: 'start', width: '100%', display: 'flex', alignItems: 'center' }}
                  >
                    {/* Stage number accent */}
                    <Box sx={{
                      width: 44, alignSelf: 'stretch',
                      background: stage.inactive
                        ? grey[300]
                        : 'linear-gradient(to bottom, #1976d2, #0d47a1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: 700, fontSize: 18,
                    }}>
                      {stageIndex + 1}
                    </Box>
                    <Box sx={{ flex: 1, p: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {stage.title}
                      </Typography>
                      {stage.inactive ? (
                        <Typography variant="caption" sx={{ color: 'error.main' }}>DELETED</Typography>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {stage.targets?.paper || 0} {t('app.qsTargets').toLowerCase()}
                          </Typography>
                          <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: grey[400] }} />
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {stage.targets?.poppers || 0} {t('app.qsPoppers').toLowerCase()}
                          </Typography>
                          {scoreCount > 0 && (
                            <>
                              <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: grey[400] }} />
                              <Box sx={{
                                bgcolor: 'primary.main', color: 'white',
                                borderRadius: 1, px: 0.75, py: 0.125,
                                fontSize: 11, fontWeight: 700,
                              }}>
                                {scoreCount} {t('app.qsScores').toLowerCase()}
                              </Box>
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                    {!stage.inactive && <ChevronRight sx={{ color: grey[400], mr: 1 }} />}
                  </ButtonBase>
                </Paper>
              );
            })}

            {/* Add stage button */}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={onNewStage}
              sx={{
                borderRadius: 2.5, py: 1.5, mt: 0.5,
                textTransform: 'none', fontWeight: 600,
              }}
            >
              {t('app.qsNewStage')}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}
